import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE_FRAME } from "./blockSurface";

/**
 * Stat - KPI / metric card. Renderer for the K2 `stat` block
 * (`docs/contracts/block-registry.md`): the platform's chat
 * `BlockRenderer` passes a `stat` block's `props` straight through, so
 * the prop names match the block payload exactly.
 *
 * Block props (`{ label, value, delta?, unit? }`):
 *   - `label` - the metric's name (e.g. "Monthly revenue"). Required.
 *   - `value` - the headline number or string (e.g. `42_300`, `"99.9"`).
 *     Required. Numbers render with locale grouping; strings render as-is.
 *   - `delta?` - period-over-period change. A bare FINITE number is read as a
 *     percentage; a preformatted string (e.g. "+3.2pp") renders verbatim; pass
 *     `{ value, unit?, direction? }` for finer control. Positive reads as up /
 *     good, negative as down / bad, by default. A non-finite number (NaN /
 *     Infinity) renders NO delta rather than "NaN%" (#240).
 *   - `unit?` - suffix appended to the value (e.g. `"%"`, `"ms"`, `"req/s"`).
 *
 * Client-only props (never part of the streamed block): `className`,
 * `loading`, `error`, `direction`, `locale`.
 *
 * States:
 *   - `loading` - a token-tinted skeleton in the card's shape.
 *   - `error` - a destructive-tinted inline message (the thread never
 *     crashes on a bad block; the registry contract requires a safe render).
 *
 * Theming is entirely token-driven (`bg-card`, `text-foreground`,
 * `text-muted-foreground`, `text-success`, `text-destructive`, …) so
 * per-studio retint + dark mode work with no hardcoded colors. SSR-safe:
 * no `window` / `document` access at module top or render.
 */

const statVariants = cva(
  cn(BLOCK_SURFACE_FRAME, "flex flex-col gap-1.5", "min-w-[10rem]"),
  {
    variants: {
      // Padding scales with size; the `default`/`lg` insets stay >= the surface
      // radius so the metric never crowds the rounded corner. `sm` is the dense
      // dashboard tile where a tighter inset is intentional.
      size: {
        sm: "p-4 gap-1",
        default: "p-5 gap-1.5",
        lg: "p-6 gap-2",
      },
    },
    defaultVariants: { size: "default" },
  },
);

/**
 * Direction the delta points. `auto` (default) derives it from the sign of
 * the delta value; `up` / `down` force it; `neutral` renders flat (no
 * color, no arrow).
 */
export type StatDeltaDirection = "auto" | "up" | "down" | "neutral";

/** Structured delta. A bare `number` is sugar for `{ value }` as a percent. */
export interface StatDelta {
  /** The change amount. Sign drives `auto` direction + color. */
  value: number;
  /** Unit suffix for the delta. Defaults to `"%"`. Pass `""` for none. */
  unit?: string;
  /**
   * Whether an upward move reads as good. Default `true`. When `false` (e.g.
   * latency, error rate, churn), a positive delta renders in the "bad" color.
   */
  upIsGood?: boolean;
  /** Optional trailing annotation, e.g. `"vs last week"`. */
  label?: string;
}

export interface StatProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof statVariants> {
  /** Metric name. Required. */
  label: string;
  /** Headline value. Numbers get locale grouping; strings render verbatim. */
  value: string | number;
  /** Suffix appended to the value (e.g. `"%"`, `"ms"`). */
  unit?: string;
  /**
   * Period-over-period change. A bare finite number is read as a percentage; a
   * preformatted string renders verbatim; a StatDelta gives finer control. A
   * non-finite number (NaN / Infinity) renders NO delta (#240).
   */
  delta?: number | string | StatDelta;
  /** Force the delta direction. Overrides the sign-derived default. */
  direction?: StatDeltaDirection;
  /** Render the loading skeleton instead of content. */
  loading?: boolean;
  /** Render an error message instead of content. Pass `true` or a string. */
  error?: boolean | string;
  /** BCP-47 locale for number grouping. Defaults to the host's locale. */
  locale?: string;
}

function resolveLocale(locale?: string): string | undefined {
  if (locale) return locale;
  // Read the host locale only at render on the client; SSR falls back to
  // the runtime default (undefined => Node's default locale) so output is
  // deterministic across the hydration boundary for ASCII-grouped numbers.
  if (typeof navigator !== "undefined") return navigator.language;
  return undefined;
}

function formatValue(value: string | number, locale?: string): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString(locale);
  }
  return String(value);
}

export const Stat = React.forwardRef<HTMLDivElement, StatProps>(function Stat(
  {
    className,
    size,
    label,
    value,
    unit,
    delta,
    direction = "auto",
    loading = false,
    error = false,
    locale,
    ...rest
  },
  ref,
) {
  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(statVariants({ size }), className)}
        aria-busy="true"
        data-slot="stat"
        {...rest}
      >
        <div className="h-3 w-20 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        <div className="h-7 w-28 animate-pulse rounded bg-muted motion-reduce:animate-none" />
      </div>
    );
  }

  if (error) {
    const message = typeof error === "string" ? error : "Couldn't load this metric.";
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          statVariants({ size }),
          "border-destructive/30 bg-destructive/5",
          className,
        )}
        data-slot="stat"
        {...rest}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="text-sm text-destructive">{message}</span>
      </div>
    );
  }

  const resolvedLocale = resolveLocale(locale);
  const formatted = formatValue(value, resolvedLocale);
  const deltaView = resolveDeltaView(delta, direction);

  return (
    <div
      ref={ref}
      className={cn(statVariants({ size }), className)}
      data-slot="stat"
      {...rest}
    >
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            "font-mono font-semibold tabular-nums leading-none text-foreground",
            size === "sm" ? "text-2xl" : size === "lg" ? "text-4xl" : "text-3xl",
          )}
        >
          {formatted}
          {unit ? (
            <span className="ml-1 align-baseline text-[0.5em] font-medium text-muted-foreground">
              {unit}
            </span>
          ) : null}
        </span>
        {deltaView ? (
          <DeltaIndicator view={deltaView} locale={resolvedLocale} />
        ) : null}
      </div>
    </div>
  );
});

Stat.displayName = "Stat";

/* ============================================================================
 * Internal - the delta indicator
 *
 * Atlas technical language (LOCKS section B R5.3): the delta reads via mono
 * numerals plus a status COLOR, not a decorative filled pill. Status is carried
 * by copy + color the same way the shared tool-status chip carries lifecycle
 * status, so the card adds signal, not chrome.
 * ========================================================================= */

type ResolvedDirection = Exclude<StatDeltaDirection, "auto">;

type DeltaView =
  | { kind: "numeric"; delta: StatDelta; direction: StatDeltaDirection }
  | { kind: "preformatted"; text: string; direction: ResolvedDirection };

/**
 * Normalize the raw `delta` prop into a renderable view, or `undefined` when
 * there is nothing safe to show. Drops a non-finite number (NaN / Infinity) so
 * the card never renders "NaN%" (#240); a string renders verbatim, coloured by
 * its leading sign.
 */
function resolveDeltaView(
  delta: number | string | StatDelta | undefined,
  direction: StatDeltaDirection,
): DeltaView | undefined {
  if (delta == null) return undefined;

  if (typeof delta === "string") {
    const text = delta.trim();
    if (!text) return undefined;
    const resolved: ResolvedDirection =
      direction !== "auto"
        ? direction
        : text.startsWith("+")
          ? "up"
          : text.startsWith("-")
            ? "down"
            : "neutral";
    return { kind: "preformatted", text, direction: resolved };
  }

  const structured: StatDelta = typeof delta === "number" ? { value: delta } : delta;
  // Drop a non-finite magnitude rather than render "NaN%" / "Infinity%".
  if (!Number.isFinite(structured.value)) return undefined;
  return { kind: "numeric", delta: structured, direction };
}

function directionFor(value: number, direction: StatDeltaDirection): ResolvedDirection {
  if (direction !== "auto") return direction;
  return value > 0 ? "up" : value < 0 ? "down" : "neutral";
}

/**
 * Status colour for a direction. "Good" vs "bad" maps onto the success /
 * destructive tokens; whether up is good is consumer-controlled (latency /
 * churn invert it). Neutral is muted. Token-only - no hardcoded colour.
 */
function toneFor(direction: ResolvedDirection, upIsGood: boolean): string {
  if (direction === "neutral") return "text-muted-foreground";
  const good = upIsGood ? direction === "up" : direction === "down";
  return good ? "text-success" : "text-destructive";
}

function DeltaIndicator({ view, locale }: { view: DeltaView; locale?: string }) {
  if (view.kind === "preformatted") {
    // A caller-shaped string (e.g. "+3.2pp"): render verbatim, coloured by the
    // sign we inferred. upIsGood is unknowable for an opaque string, so a
    // leading "+" reads as good by convention.
    return (
      <span className={cn("font-mono text-xs tabular-nums", toneFor(view.direction, true))}>
        {view.text}
      </span>
    );
  }

  const { value, unit = "%", upIsGood = true, label } = view.delta;
  const resolvedDirection = directionFor(value, view.direction);
  const tone = toneFor(resolvedDirection, upIsGood);
  const arrow =
    resolvedDirection === "up" ? "+" : resolvedDirection === "down" ? "-" : "";
  const magnitude = Math.abs(value).toLocaleString(locale);

  return (
    <span className={cn("inline-flex items-baseline gap-1 text-xs", tone)}>
      <span aria-hidden className="font-mono tabular-nums">
        {arrow}
        {magnitude}
        {unit}
      </span>
      <span className="sr-only">
        {resolvedDirection === "up"
          ? "up"
          : resolvedDirection === "down"
            ? "down"
            : "no change"}{" "}
        {magnitude}
        {unit}
      </span>
      {label ? (
        <span className="font-normal text-muted-foreground">{label}</span>
      ) : null}
    </span>
  );
}

export { statVariants };
export type StatVariantProps = VariantProps<typeof statVariants>;
